import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGoogleLogin } from "@react-oauth/google";
import { useState, useCallback } from "react";
import { axiosPrivate } from "@/api/axios";

interface AddScopesResponse {
  success: boolean;
}

const addScopes = async (code: string, userId: string): Promise<AddScopesResponse> => {
  const response = await axiosPrivate.post(
    "/api/auth/add-scopes",
    { code, userId }
  );
  return response.data;
};

export const useIncrementalAuth = (userId: string) => {
  const queryClient = useQueryClient();
  const [pendingResolve, setPendingResolve] = useState<{
    resolve: (value: string) => void;
    reject: (error: any) => void;
  } | null>(null);

  const mutation = useMutation({
    mutationFn: async (code: string) => addScopes(code, userId),
    onSuccess: (_, code) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["task-fusion-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      queryClient.invalidateQueries({ queryKey: ["task-event-mappings"] });
      
      if (pendingResolve) {
        pendingResolve.resolve(code);
        setPendingResolve(null);
      }
    },
    onError: (error) => {
      if (pendingResolve) {
        pendingResolve.reject(error);
        setPendingResolve(null);
      }
    },
  });

  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        await mutation.mutateAsync(codeResponse.code);
      } catch (error) {
        // Error handled in mutation.onError
      }
    },
    onError: (error) => {
      if (pendingResolve) {
        pendingResolve.reject(error);
        setPendingResolve(null);
      }
    },
    flow: "auth-code",
    ux_mode: "popup",
    select_account: true,
    scope: "https://www.googleapis.com/auth/calendar",
  });

  const triggerAuth = useCallback(() => {
    return new Promise<string>((resolve, reject) => {
      setPendingResolve({ resolve, reject });
      googleLogin();
    });
  }, [googleLogin]);

  return {
    ...mutation,
    triggerAuth,
  };
};

